import { AbstractMediaEditor } from ".";
import _ from "lodash";
import * as React from "react";
import * as Comp from "../../../../components";
import { IComponentWithValidatedForm } from "../../../../components/validation";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { Api, ApiEndpoint } from "../../../../services/Api";
import Settings from "../../../../settings";
import { ReactHelper, Utility } from "../../../../utils";
// @ts-ignore
import * as expr from "property-expr";

interface OwnProps {
    abstractGuid: string;
    abstractTypeMedia: Forms.Abstract.AbstractTypeMediaData;
    api: Api;
    formComponent: IComponentWithValidatedForm;
    parentFieldName: string;
    type?: M.Abstract.AbstractType;
    onMediaDeleted: (abstractMediaId: number) => void;
    onUploadCompleted: (abstractTypeMediaId: number) => void;
    onUploadStarted: (abstractTypeMediaId: number) => void;
    subscribeToMediaProcessing: (mediaId: number) => void;
    updateAbstractMedia: (media: M.Abstract.AbstractMedia) => void;
}

interface OwnState {
}

const numberToWordMap = {
    0: "Zero",
    1: "One",
    2: "Two",
    3: "Three",
    4: "Four",
    5: "Five",
    6: "Six",
    7: "Seven",
    8: "Eight",
    9: "Nine",
    10: "Ten",
};

export class AbstractTypeMediaEditor extends React.Component<OwnProps, OwnState> {

    private _confirmDelete: Comp.Modal.ConfirmModal | null = null;
    private _isMounted = false;

    public componentDidMount() {
        this._isMounted = true;
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const atm = this.props.abstractTypeMedia;
        const namePrefix = this.getNamePrefix();
        const maxMsg = !!atm.maxUploads && atm.maxUploads > 1
            ? <p>Maximum {(numberToWordMap[atm.maxUploads] || atm.maxUploads).toString().toLowerCase()}</p>
            : null;

        const errors = this.props.parentFieldName
            ? this.props.formComponent.getFieldErrors(this.props.parentFieldName)
            : [];

        const label = atm.label;
        const required = atm.requiredForSubmit && this.props.type !== undefined && this.props.type.uploadDeadline === null;

        const addMediaBtn = (!!atm.maxUploads && atm.media.filter((m) => !m.deleted).length < atm.maxUploads) || !atm.maxUploads
            ? <p>
                <a className="btn btn-default" href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.onMediaAdd(); }}>
                    Upload another file&hellip;
                </a>
            </p>
            : null;

        const media = atm.media
            .filter((m) => !m.deleted)
            .map((m, i) => {
                const uploadUrl = Settings.ApiUrl + ApiEndpoint.getFormattedUrl(Api.endpoints.abstract.upload.withUrlParams({ guid: this.props.abstractGuid }));

                return m.id > 0
                    ? <AbstractMediaEditor
                        key={i}
                        api={this.props.api}
                        errors={errors}
                        formComponent={this.props.formComponent}
                        media={m}
                        typeMedia={atm}
                        namePrefix={`${namePrefix}media[${m.index}].`}
                        onRemove={() => this.toggleRemoveConfirmation(m)}
                    />
                    : <Comp.Generic.WrapperLess key={i}>
                        <p className="mute">
                            Allowed file extension(s): {Utility.joinSkipEmpty(atm.mediaTypeExtns.split(",").filter((e) => e !== ""), ", ")}
                            {/* <br />Maximum file size: {Utility.formatFileSize(atm.maxSize, 0, true)} */}
                        </p>
                        <Comp.Upload.FileUploader
                            uploadOnSelect
                            uploader={{
                                chunk_size: atm.mediaTypeId === M.Upload.MediaTypes.Video.id ? "10mb" : "5mb",
                                events: {
                                    beforeUpload: () => this.props.onUploadStarted(atm.id),
                                    uploadComplete: () => this.props.onUploadCompleted(atm.id),
                                },
                                multipart_params: {
                                    abstractTypeMediaId: atm.id,
                                    mediaId: m.mediaId,
                                },
                                filters: {
                                    max_file_size: atm.maxSize,
                                    mime_types: [{ title: atm.mediaType, extensions: atm.mediaTypeExtns }],
                                    prevent_duplicates: true,
                                },
                                url: uploadUrl,
                            }}
                            onUploadComplete={(upload) => this.onUploadComplete(m.index, upload as M.Abstract.AbstractMedia)}
                        />
                    </Comp.Generic.WrapperLess>;
            });

        return <Comp.Editors.FormField name="" label={label} labelHint={atm.labelHint} description={atm.description} descriptionIsHtml required={required}>
            <Comp.Modal.ConfirmModal
                headerClassName="warning"
                onProceed={() => this.handleUploadRemove()}
                title="Remove upload"
                ref={(ref) => this._confirmDelete = ref}
            >
                <p>Are you sure you want to remove this upload?</p>
            </Comp.Modal.ConfirmModal>

            <input type="hidden" name={this.props.parentFieldName} />
            <Comp.Editors.FormFieldErrors errors={errors} />
            {maxMsg}
            {media}
            {addMediaBtn}
        </Comp.Editors.FormField>;
    }

    private getNamePrefix() {
        return this.props.parentFieldName
            ? this.props.parentFieldName + "."
            : "";
    }

    private getMediaType(formModel: M.Validation.FormValidationModel<any>) {
        if (this.props.parentFieldName) {
            const getter = expr.getter(this.props.parentFieldName, formModel.data);
            return getter(formModel.data);
        }
        else
            return formModel.data;
    }

    private async handleUploadRemove() {
        if (this._confirmDelete === null)
            return;

        const mediaForm = this._confirmDelete.getRef() as Forms.Abstract.AbstractMediaData;
        if (mediaForm !== undefined) {
            const result = await this.props.api.delete(Api.endpoints.abstract.media.withUrlParams({ id: mediaForm.id }));
            if (result.isSuccess) {
                this.onMediaDelete(mediaForm.index);
                this.props.onMediaDeleted(mediaForm.id);
            }
            else {

            }

            if (this._confirmDelete)
                this._confirmDelete.close();
        }
    }

    private onMediaAdd() {
        const formModel = this.props.formComponent.getFormModel();
        const mediaType = this.getMediaType(formModel) as Forms.Abstract.AbstractTypeMediaData;

        mediaType.media.push({
            ...Forms.Abstract.DefaultAbstractMedia,
            index: mediaType.media.length,
        });

        this.props.formComponent.updateFormModel(formModel);
    }

    private onMediaDelete(index: number) {
        const formModel = this.props.formComponent.getFormModel();
        const mediaType = this.getMediaType(formModel) as Forms.Abstract.AbstractTypeMediaData;

        const media = _.find(mediaType.media, (m) => m.index === index);
        if (media) {
            media.deleted = true;
            this.props.formComponent.updateFormModel(formModel);
        }
    }

    private onUploadComplete(index: number, upload: M.Abstract.AbstractMedia) {
        if (!this._isMounted)
            return;

        const formModel = this.props.formComponent.getFormModel();
        const mediaType = this.getMediaType(formModel) as Forms.Abstract.AbstractTypeMediaData;

        const media = _.find(mediaType.media, (m) => m.index === index);
        if (media) {
            media.id = upload.abstractMediaId;
            media.fileName = upload.fileName;
            media.isImage = upload.isImage;
            media.isVideo = upload.isVideo;
            media.isProcessing = upload.isProcessing;
            media.mediaId = upload.id;
            media.thumbMd = upload.thumbMd;
            media.thumbSm = upload.thumbSm;
            media.typeId = upload.typeId;
            media.url = upload.url;

            this.props.formComponent.updateFormModel(formModel, () => {
                this.props.updateAbstractMedia(upload);
                if (upload.isProcessing)
                    this.props.subscribeToMediaProcessing(upload.id);
            });
        }
    }

    private toggleRemoveConfirmation(mediaForm: Forms.Abstract.AbstractMediaData) {
        if (this._confirmDelete !== null)
            this._confirmDelete.show(mediaForm);
    }
}



// WEBPACK FOOTER //
// ./src/containers/abstracts/composer/components/AbstractTypeMediaEditor.tsx